import React from 'react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import eventRefs from '../../assets/eventRef';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './eventLogs.css';
import {
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from '../../components/common/appShell';
import {
  TIMEFRAME_PRESETS,
  getDefaultTimeRange,
  createRangeFromDuration,
  formatTimeRangeLabel,
  findTimeframePreset,
} from '../../utils/timeRange';
import {
  buildCustomFunctionDescription,
  EVENT_ALERT_KIND,
  getCmpConnectionCurrentStateNote,
  getCmpConnectionStatusColor,
  getCmpConnectionStatusLabel,
  getCustomFunctionOperationLabel,
  getCustomFunctionStatusColor,
  getCustomFunctionStatusLabel,
  isCmpConnectionLog,
  isCustomFunctionResultLog,
} from '../../utils/eventLogKinds';
import { buildAlertDetailsPath } from '../../utils/deviceAlertUtils';

const EVENT_REF_MAP = new Map((eventRefs || []).map((ref) => [String(ref.code), ref]));
const EVENT_GROUP_ORDER = [
  'Network & Ping',
  'Inlet Events',
  'Outlet Events',
  'Bank Events',
  'Environmental Events',
  'Sensor & IO Events',
  'Scheduled Events',
  'ATS Events',
  'Other Events',
];

function categorizeEventGroup(ref) {
  if (Number(ref?.code) === 1048267) {
    return 'Network & Ping';
  }
  const name = (ref?.name || '').toLowerCase();
  if (name.includes('autoping') || name.includes('network') || name.includes('ping')) {
    return 'Network & Ping';
  }
  if (name.startsWith('inlet')) return 'Inlet Events';
  if (name.startsWith('outlet')) return 'Outlet Events';
  if (name.startsWith('bank')) return 'Bank Events';
  if (name.includes('temperature') || name.includes('humidity')) return 'Environmental Events';
  if (
    name.includes('sensor') ||
    name.includes('digital') ||
    name.includes('switch') ||
    name.includes('hub')
  ) {
    return 'Sensor & IO Events';
  }
  if (name.includes('schedule')) return 'Scheduled Events';
  if (name.includes('ats')) return 'ATS Events';
  return 'Other Events';
}

const EVENT_LOG_DEFAULT_TIMEFRAME = '30d';

const buildInitialTimeRangeState = () => {
  const range = getDefaultTimeRange(EVENT_LOG_DEFAULT_TIMEFRAME);
  return {
    timeRangeKey: EVENT_LOG_DEFAULT_TIMEFRAME,
    appliedRange: range,
    customRange: [
      range.since ? new Date(range.since) : null,
      range.before ? new Date(range.before) : null,
    ],
    customError: '',
  };
};

function getEventLogSearchFromLocation(props = {}) {
  const rawSearch =
    props.location?.search ||
    (typeof window !== 'undefined' ? window.location.search : '');

  return new URLSearchParams(rawSearch || '').get('search') || '';
}

export default class LoggingEventLogs extends React.Component {
  state = {
    page: 1,
    limit:
      Number(this.props.initialLimit) > 0 ? Number(this.props.initialLimit) : 20,
    query: getEventLogSearchFromLocation(this.props),
    searchText: getEventLogSearchFromLocation(this.props),
    kind: 'all',
    eventTypes: [],
    alertsOnly: false,
    eventId: '',
    ...buildInitialTimeRangeState(),
    deviceLogs: [],
    deviceMeta: {},
    deviceLoading: false,
    exportingCsv: false,
    dark: typeof document !== 'undefined' && document.body.classList.contains('dark-mode'),
  };

  getScopedSerial = (props = this.props) => {
    const rawSerial =
      props.device?.enclosureSerialNumber ??
      props.queryParams?.enclosure_serial_number ??
      props.match?.params?.enclosureSerialNumber;

    if (rawSerial === undefined || rawSerial === null || rawSerial === '') {
      return null;
    }

    return String(rawSerial);
  };

  getFixedEventId = (props = this.props) => {
    const rawEventId =
      props.fixedEventId ??
      props.queryParams?.eventId ??
      null;

    if (rawEventId === undefined || rawEventId === null || rawEventId === '') {
      return '';
    }

    return String(rawEventId);
  };

  componentDidMount() {
    const params = new URLSearchParams(this.props.location?.search || '');
    const eventId = this.getFixedEventId() || params.get('eventId') || '';
    if (eventId) {
      this.setState(
        (prevState) => (prevState.eventId === eventId ? null : { eventId }),
        () => this.fetchPage(1)
      );
    } else {
      this.fetchPage(1);
    }
    if (typeof document !== 'undefined') {
      this.darkObserver = new MutationObserver(() => {
        const isDark = document.body.classList.contains('dark-mode');
        this.setState((prev) => (prev.dark === isDark ? null : { dark: isDark }));
      });
      this.darkObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  componentDidUpdate(prevProps) {
    const prevSerialStr = this.getScopedSerial(prevProps);
    const nextSerialStr = this.getScopedSerial(this.props);

    if (nextSerialStr !== prevSerialStr) {
      this.setState(
        {
          deviceLogs: [],
          deviceMeta: {},
          deviceLoading: false,
        },
        () => this.fetchPage(1)
      );
      return;
    }

    const prevFixedEventId = this.getFixedEventId(prevProps);
    const nextFixedEventId = this.getFixedEventId(this.props);
    if (nextFixedEventId !== prevFixedEventId) {
      this.setState({ eventId: nextFixedEventId || '' }, () => this.fetchPage(1));
      return;
    }

    const prevLocationParams = new URLSearchParams(prevProps.location?.search || '');
    const nextLocationParams = new URLSearchParams(this.props.location?.search || '');
    const prevSearchEventId = prevFixedEventId || prevLocationParams.get('eventId') || '';
    const nextSearchEventId = nextFixedEventId || nextLocationParams.get('eventId') || '';
    const prevUrlSearchText = prevLocationParams.get('search') || '';
    const nextUrlSearchText = nextLocationParams.get('search') || '';

    if (
      nextSearchEventId !== prevSearchEventId ||
      nextUrlSearchText !== prevUrlSearchText
    ) {
      this.setState(
        {
          eventId: nextSearchEventId || '',
          query: nextUrlSearchText,
          searchText: nextUrlSearchText,
        },
        () => this.fetchPage(1)
      );
    }
  }

  componentWillUnmount() {
    this.darkObserver && this.darkObserver.disconnect();
  }

  getTimeRangeParams() {
    const { appliedRange } = this.state;
    const params = {};
    if (appliedRange && Number.isFinite(appliedRange.since)) {
      params.since = Math.floor(appliedRange.since);
    }
    if (appliedRange && Number.isFinite(appliedRange.before)) {
      params.before = Math.floor(appliedRange.before);
    }
    return params;
  }

  handleTimePresetChange = (event) => {
    const nextKey = event?.target?.value || EVENT_LOG_DEFAULT_TIMEFRAME;
    const currentKey = this.state.timeRangeKey;
    if (nextKey === currentKey) return;
    if (nextKey === 'custom') {
      this.setState({ timeRangeKey: nextKey, customError: '' });
      return;
    }
    const preset = findTimeframePreset(nextKey);
    let range;
    if (preset?.key === 'all') {
      range = { since: null, before: null };
    } else {
      const duration = preset?.durationMs || null;
      range = duration ? createRangeFromDuration(duration) : this.state.appliedRange;
    }
    this.setState(
      {
        timeRangeKey: nextKey,
        appliedRange: range,
        customRange: [
          range.since ? new Date(range.since) : null,
          range.before ? new Date(range.before) : null,
        ],
        customError: '',
      },
      () => this.fetchPage(1)
    );
  };

  handleCustomStartChange = (startDate) => {
    this.setState((prevState) => {
      const [, endDate] = prevState.customRange || [];
      return { customRange: [startDate || null, endDate || null], customError: '' };
    });
  };

  handleCustomEndChange = (endDate) => {
    this.setState((prevState) => {
      const [startDate] = prevState.customRange || [];
      return { customRange: [startDate || null, endDate || null], customError: '' };
    });
  };

  handleApplyCustomRange = () => {
    const [start, end] = this.state.customRange || [];
    if (!start || !end) {
      this.setState({ customError: 'Select a start and end time.' });
      return;
    }
    if (start.getTime() >= end.getTime()) {
      this.setState({ customError: 'Start time must be before end time.' });
      return;
    }
    const range = { since: start.getTime(), before: end.getTime() };
    this.setState(
      {
        appliedRange: range,
        customRange: [start, end],
        customError: '',
        timeRangeKey: 'custom',
      },
      () => this.fetchPage(1)
    );
  };

  handleResetCustomRange = () => {
    const { appliedRange } = this.state;
    const start = appliedRange?.since ? new Date(appliedRange.since) : new Date();
    const end = appliedRange?.before ? new Date(appliedRange.before) : new Date();
    this.setState({ customRange: [start, end], customError: '' });
  };

  buildQueryParams = ({ page, limit, includePagination = true } = {}) => {
    const {
      query,
      kind,
      eventTypes,
      alertsOnly,
      eventId,
    } = this.state;
    const base = {
      search: query || '',
      kind: kind === 'all' ? '' : kind,
      eventType: (eventTypes || []).join(','),
      triggered: alertsOnly ? 'true' : '',
      eventId: eventId || '',
      ...this.getTimeRangeParams(),
      ...(this.props.queryParams || {}),
    };

    if (includePagination) {
      base.page = page != null ? page : this.state.page;
      base.limit = limit != null ? limit : this.state.limit;
    }

    return base;
  };

  handleExportCsv = async () => {
    if (this.state.exportingCsv) {
      return;
    }
    this.setState({ exportingCsv: true });
    try {
      const params = this.buildQueryParams({ includePagination: false });
      const res = await axios.get('/v2/eventLogs/export', {
        params,
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = url;
      link.download = `event-logs-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export event logs CSV', error);
    } finally {
      this.setState({ exportingCsv: false });
    }
  };

  handleEventTypeSelect = (event) => {
    const value = event.target.value;
    if (!value) return;
    if (this.state.eventTypes.includes(value)) {
      event.target.value = '';
      return;
    }
    this.handleFilterChange({ eventTypes: [...this.state.eventTypes, value] });
    event.target.value = '';
  };

  handleRemoveEventType = (code) => {
    this.handleFilterChange({
      eventTypes: this.state.eventTypes.filter((c) => c !== code),
    });
  };

  getEventTypeGroups() {
    const groups = new Map();
    (eventRefs || []).forEach((ref) => {
      if (!ref || typeof ref.code === 'undefined') return;
      const groupLabel = categorizeEventGroup(ref);
      if (!groups.has(groupLabel)) groups.set(groupLabel, []);
      groups.get(groupLabel).push({
        value: String(ref.code),
        label: ref.name || String(ref.code),
      });
    });
    const ordered = EVENT_GROUP_ORDER.filter((label) => groups.has(label));
    const leftovers = Array.from(groups.keys())
      .filter((label) => !EVENT_GROUP_ORDER.includes(label))
      .sort();
    return [...ordered, ...leftovers].map((label) => ({
      label,
      options: (groups.get(label) || []).sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }

  fetchPage = (page) => {
    const { fetchResourcePaginate } = this.props;
    const scopedSerial = this.getScopedSerial();
    const params = this.buildQueryParams({ page });
    this.setState({ page });
    if (scopedSerial) {
      this.setState({ deviceLoading: true });
      axios
        .get(`/v2/eventLogs`, {
          params: {
            ...params,
            enclosure_serial_number: scopedSerial,
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        })
        .then((res) => {
          this.setState({
            deviceLogs: res.data.data,
            deviceMeta: res.data.meta,
            deviceLoading: false,
          });
        })
        .catch((err) => {
          console.error('Error fetching device event logs', err);
          this.setState({ deviceLoading: false });
        });
    } else if (fetchResourcePaginate) {
      // If your API doesn't support these filters yet, it will ignore them.
      fetchResourcePaginate('eventLogs', undefined, {
        ...params,
      });
    }
  };

  handleFilterChange = (next) => {
    this.setState(next, () => this.fetchPage(1));
  };

  applySearch = () => {
    this.handleFilterChange({ query: this.state.searchText, eventId: '' });
  };

  clearAllFilters = () => {
    this.handleFilterChange({
      query: '',
      searchText: '',
      kind: 'all',
      eventTypes: [],
      alertsOnly: false,
      eventId: '',
    });
  };

  renderFilterChips(logs) {
    const { query, eventId, eventTypes } = this.state;
    const activeEventId = this.getFixedEventId() || eventId;
    const eventName = logs.find(log => log.eventId == activeEventId)?.name || '';
    const chips = [];

    if (query) {
      chips.push(
        <span key="search" className="event-logs__filter-pill">
          <span>Search: {query}</span>
          <button
            type="button"
            aria-label="Remove search filter"
            className="event-logs__chip-remove"
            onClick={() => this.handleFilterChange({ query: '', searchText: '' })}
          >
            <i className="material-icons" aria-hidden="true">close</i>
          </button>
        </span>
      );
    }

    if (activeEventId) {
      chips.push(
        <span
          key="event"
          className="event-logs__filter-pill"
        >
          <span>Event: {eventName || activeEventId}</span>
          <button
            type="button"
            aria-label="Remove event filter"
            className="event-logs__chip-remove"
            onClick={() =>
              this.handleFilterChange({ query: '', searchText: '', eventId: '' })
            }
          >
            <i className="material-icons" aria-hidden="true">close</i>
          </button>
        </span>
      );
    }

    (eventTypes || []).forEach((code) => {
      const label = EVENT_REF_MAP.get(String(code))?.name || `Event ${code}`;
      chips.push(
        <span key={`type-${code}`} className="event-logs__filter-pill">
          <span>Type: {label}</span>
          <button
            type="button"
            aria-label={`Remove ${label} filter`}
            className="event-logs__chip-remove"
            onClick={() => this.handleRemoveEventType(code)}
          >
            <i className="material-icons" aria-hidden="true">close</i>
          </button>
        </span>
      );
    });

    return chips;
  }

  renderRelativeTime(ts) {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    return formatDistanceToNow(d, { addSuffix: true });
  }

  renderTimestamp(ts) {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    return format(d, 'PP p');
  }

  statusDot(connected) {
    return (
      <i
        className="material-icons"
        title={connected ? 'Online' : 'Offline'}
        style={{
          fontSize: 12,
          verticalAlign: 'middle',
          margin: "0px",
          width: "18px",
          // marginRight: 6,
          color: connected ? 'forestgreen' : '#9e9e9e',
        }}
      >
        fiber_manual_record
      </i>
    );
  }

  deviceCell(log) {
    const connected = log.connectionState === 'Connected';
    const label = log.deviceName || log.enclosureSerialNumber;
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          {this.statusDot(connected)}
          <Link
            to={`/devices/connected/${log.enclosureSerialNumber}/overview`}
            style={{ textDecoration: 'underline' }}
            title="Open device"
          >
            {label}
          </Link>
        </div>
        <div style={{ fontSize: '80%', color: '#666', display: 'flex', gap: 8, marginTop: 2 }}>
          <span>SN: {log.enclosureSerialNumber}</span>
          {/* <button
            type="button"
            aria-label="Copy serial"
            title="Copy serial"
            onClick={() => navigator.clipboard?.writeText(String(log.enclosureSerialNumber))}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <i className="material-icons" style={{ fontSize: 14, color: '#666' }}>
              content_copy
            </i>
          </button> */}
        </div>
      </div>
    );
  }

  renderStatus(log) {
    if (isCustomFunctionResultLog(log)) {
      return (
        <span style={{ color: getCustomFunctionStatusColor(log.actionStatus) }}>
          {getCustomFunctionStatusLabel(log.actionStatus)}
        </span>
      );
    }
    if (isCmpConnectionLog(log)) {
      return (
        <span style={{ color: getCmpConnectionStatusColor(log.triggered) }}>
          {getCmpConnectionStatusLabel(log.triggered)}
        </span>
      );
    }

    const isActive = !!log.triggered;
    return (
      <span style={{ color: isActive ? 'maroon' : 'forestgreen' }}>
        {isActive ? 'Active' : 'Cleared'}
      </span>
    );
  }

  renderType(log) {
    const ref = eventRefs.find((e) => e.code == log.eventType);
    const name = ref ? ref.name : log.eventType;
    return name || '';
  }

  renderDetails(log) {
    if (isCustomFunctionResultLog(log)) {
      return buildCustomFunctionDescription(log);
    }

    const ref = eventRefs.find((e) => e.code == log.eventType);
    let params = [];
    try {
      params = Array.isArray(log.params)
        ? log.params
        : log.params
        ? JSON.parse(log.params)
        : [];
    } catch (e) {
      params = [];
    }
    if (ref) {
      const tpl = log.triggered ? ref.triggeredDescription : ref.notTriggeredDescription;
      if (tpl) {
        const description = tpl.replace(/{{param(\d+)}}/g, (_, i) => {
          const idx = parseInt(i, 10) - 1;
          let val = params[idx] !== undefined ? String(params[idx]) : '';
          if (log.eventType == 12 && idx === 2) {
            if (val === '1') return 'primary ethernet';
            if (val === '2') return 'USB secondary ethernet';
            if (val === '' ) return "primary ethernet"
          }
          return val;        
        });
        if (isCmpConnectionLog(log)) {
          const stateNote = getCmpConnectionCurrentStateNote(log);
          return stateNote ? `${description} ${stateNote}` : description;
        }
        return description;
      }
    }
    return params.length ? JSON.stringify(params) : '';
  }

  parseActionNames(actions) {
    // Accepts:
    // - array of strings: ["rebootOutlet", "email"]
    // - array of objects: [{name:"rebootOutlet",...}]
    // - object or JSON string of either form
    let a = actions;
    if (typeof a === 'string') {
      try {
        a = JSON.parse(a);
      } catch {
        a = [];
      }
    }
    if (!a) return [];
    if (Array.isArray(a)) {
      return a
        .map((v) => (typeof v === 'string' ? v : v && typeof v.name === 'string' ? v.name : null))
        .filter(Boolean);
    }
    if (typeof a === 'object') {
      // if object like {actions:[...]} or keyed map
      if (Array.isArray(a.actions)) {
        return this.parseActionNames(a.actions);
      }
      // if map of {rebootOutlet: {...}, email: {...}}
      return Object.keys(a);
    }
    return [];
  }

  getEventRef(log) {
    if (!log) return null;
    return EVENT_REF_MAP.get(String(log.eventType)) || null;
  }

  stripTrailingVerbs(text) {
    if (!text) return '';
    return text.replace(/\b(Triggered|Cleared|Activated|Surpassed|Resolved|Updated)\b$/i, '').trim();
  }

  getOperationBaseName(log) {
    const ref = this.getEventRef(log);
    if (isCmpConnectionLog(log)) {
      const cmpBase = ref?.operationName || log?.name || ref?.name || '';
      return this.stripTrailingVerbs(cmpBase);
    }

    const base = ref?.name || ref?.operationName || log.eventType || log.name || '';
    return this.stripTrailingVerbs(base);
  }

  getOperationVerbs(ref, baseName, log) {
    if (isCmpConnectionLog(log)) {
      return {
        triggered: (ref?.operationVerbs?.triggered || 'Activated').trim(),
        cleared: (ref?.operationVerbs?.cleared || 'Cleared').trim(),
      };
    }

    return {
      triggered: 'Activated',
      cleared: 'Cleared',
    };
  }

  getOperationPhrase(log, baseName) {
    if (isCustomFunctionResultLog(log)) {
      return getCustomFunctionOperationLabel(log.actionStatus);
    }

    const ref = this.getEventRef(log);
    const verbs = this.getOperationVerbs(ref, baseName, log);
    const word = log.triggered ? verbs.triggered : verbs.cleared;
    const safeBase = baseName || log.name || ref?.name || 'Event';
    return `${safeBase} ${word}`.trim();
  }

  renderDescriptionContent(log, details) {
    const hasDetails = !!details;
    const eventLabel = log?.name || '';
    const ref = this.getEventRef(log);
    const showEventLabel = !!eventLabel && !isCmpConnectionLog(log);
    const allowLink = !!eventLabel && !!log?.eventId && !(ref && ref.disableEventLink);
    const descriptionClampLines = Number(this.props.descriptionClampLines) || 3;
    const isSingleLineDescription = descriptionClampLines <= 1;
    return (
      <div
        style={{
          display: isSingleLineDescription ? 'block' : '-webkit-box',
          WebkitLineClamp: descriptionClampLines,
          WebkitBoxOrient: isSingleLineDescription ? undefined : 'vertical',
          overflow: 'hidden',
          textOverflow: isSingleLineDescription ? 'ellipsis' : undefined,
          whiteSpace: isSingleLineDescription ? 'nowrap' : undefined,
        }}
        title={
          hasDetails
            ? `${details}${showEventLabel ? ` (${eventLabel})` : ''}`
            : eventLabel
        }
      >
        {hasDetails ? details : '—'}
        {showEventLabel && (
          <span style={{ color: '#6b7280' }}>
            {' '}
            (
            {allowLink ? (
              <Link
                to={buildAlertDetailsPath(
                  log.enclosureSerialNumber,
                  log.eventId,
                )}
                style={{ textDecoration: 'underline' }}
              >
                {eventLabel}
              </Link>
            ) : (
              <span>{eventLabel}</span>
            )}
            )
          </span>
        )}
      </div>
    );
  }

  actionsCell(log) {
    if (isCustomFunctionResultLog(log)) {
      const label = log.actionName || log.scriptName;
      if (!label) {
        return <span style={{ color: '#999' }}>—</span>;
      }

      const title =
        log.actionName && log.scriptName && log.actionName !== log.scriptName
          ? `${log.actionName} (${log.scriptName})`
          : label;

      return <span title={title}>{label}</span>;
    }
    if (isCmpConnectionLog(log)) {
      return <span style={{ color: '#999' }}>—</span>;
    }

    const names = this.parseActionNames(log.actions);
    if (!names.length) return <span style={{ color: '#999' }}>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {names.map((n) => (
          <span
            key={n}
            // className="spreadsheet-badge"
            title={n}
            // style={{ backgroundColor: '#eef2ff', color: '#1f2a6b' }}
          >
            {n}
          </span>
        ))}
      </div>
    );
  }

  render() {
    const scopedSerial = this.getScopedSerial();
    const isScopedDeviceView = Boolean(scopedSerial);
    const compactSingleEventView = Boolean(this.props.compactSingleEventView);
    const hideTitle = Boolean(this.props.hideTitle || compactSingleEventView);
    const hideSearch = Boolean(this.props.hideSearch || compactSingleEventView);
    const hideEventTypeFilter = Boolean(
      this.props.hideEventTypeFilter || compactSingleEventView
    );
    const hideExport = Boolean(this.props.hideExport || compactSingleEventView);
    const hideFilterChips = Boolean(
      this.props.hideFilterChips || compactSingleEventView
    );
    const hideClearFilters = Boolean(
      this.props.hideClearFilters || compactSingleEventView
    );
    const hideOperationFilterButton = Boolean(
      this.props.hideOperationFilterButton || compactSingleEventView
    );
    const hideAlertsOnlyToggle = Boolean(this.props.hideAlertsOnlyToggle);
    const hideSummary = Boolean(this.props.hideSummary);
    const hidePagination = Boolean(this.props.hidePagination);
    const compactTable = Boolean(this.props.compactTable);
    const logsTitle = this.props.title || 'Event Logs';
    const logsDescription =
      this.props.description ||
      'Review device event history, filter by status, type, and timeframe, and export matching rows as CSV.';
    const emptyStateText = this.props.emptyStateText || 'No event logs found';
    let logs, loading, meta;
    if (isScopedDeviceView) {
      logs = this.state.deviceLogs;
      loading = this.state.deviceLoading;
      meta = this.state.deviceMeta;
    } else {
      const {
        eventLogsPages = [],
        eventLogsMeta = {},
        eventLogsPageLoading = {},
      } = this.props.resources;

      const currentPage = eventLogsPages.find((p) => p.page === this.state.page) || {};
      logs = currentPage.eventLogs || [];
      loading = !!eventLogsPageLoading[this.state.page];
      meta = eventLogsMeta;
    }

    const currentPage = this.state.page || 1;
    const pageLimit = this.state.limit || logs.length || 1;
    const totalCount = typeof meta?.totalCount === "number" ? meta.totalCount : undefined;
    let summaryText = null;
    if (typeof totalCount === "number" && !(loading && logs.length === 0)) {
      if (totalCount === 0) {
        summaryText = "Showing 0 of 0 event logs";
      } else if (logs.length === 0) {
        summaryText = `Showing 0 of ${totalCount} event logs`;
      } else {
        const showingStart = (currentPage - 1) * pageLimit + 1;
        const showingEnd = Math.min(showingStart + logs.length - 1, totalCount);
        summaryText = `Showing ${showingStart}-${showingEnd} of ${totalCount} event logs`;
      }
    }

    const eventTypeOptions = this.getEventTypeGroups();
    const {
      kind,
      timeRangeKey,
      customRange,
      customError,
      appliedRange,
    } = this.state;
    const alertsFilterEnabled =
      kind === 'all' || kind === EVENT_ALERT_KIND;
    const rangeLabel = formatTimeRangeLabel(appliedRange?.since, appliedRange?.before);
    const summaryWithRange = [summaryText, rangeLabel].filter(Boolean).join(" · ");
    const disableCustomApply = !customRange || !(customRange[0] && customRange[1]);
    const outerClass = [
      this.props.embedded ? "" : "grid-container",
      "event-logs",
      this.props.embedded ? "event-logs--embedded" : "",
    ].filter(Boolean).join(" ");
    const innerClass = this.props.embedded ? "" : "grid-x grid-padding-x grid-padding-y align-left";
    const filterChips = hideFilterChips ? [] : this.renderFilterChips(logs);
    const canClearFilters =
      !hideClearFilters &&
      (this.state.query ||
        this.state.kind !== 'all' ||
        (this.state.eventTypes && this.state.eventTypes.length > 0) ||
        this.state.alertsOnly ||
        this.state.eventId);
    const showToolbarActions = canClearFilters || !hideExport;

    return (
      <div className={outerClass}>
        <div className={innerClass}>
          <div className="cell">
            {!hideTitle && (
              <div className="event-logs__header">
                <h2 className="event-logs__title">{logsTitle}</h2>
                <p className="event-logs__description">{logsDescription}</p>
              </div>
            )}

            <AppToolbar variant="plain" className="event-logs__toolbar">
              {!hideAlertsOnlyToggle && (
                <div
                  className="event-logs__status-filters"
                  aria-label="Event log status filters"
                >
                  <button
                    type="button"
                    className={[
                      "event-logs__status-filter",
                      !this.state.alertsOnly ? "event-logs__status-filter--active" : "",
                    ].filter(Boolean).join(" ")}
                    aria-pressed={!this.state.alertsOnly}
                    disabled={!alertsFilterEnabled}
                    onClick={() => this.handleFilterChange({ alertsOnly: false })}
                  >
                    <span>All entries</span>
                  </button>
                  <button
                    type="button"
                    className={[
                      "event-logs__status-filter",
                      this.state.alertsOnly ? "event-logs__status-filter--active" : "",
                    ].filter(Boolean).join(" ")}
                    aria-pressed={this.state.alertsOnly}
                    disabled={!alertsFilterEnabled}
                    onClick={() => this.handleFilterChange({ alertsOnly: true })}
                  >
                    <span>Triggered only</span>
                  </button>
                </div>
              )}
              {(!hideSearch || !hideEventTypeFilter || showToolbarActions) && (
                <AppToolbarGrid wide withActions className="event-logs__toolbar-grid">
                  {!hideSearch && (
                    <AppToolbarField label="Search event logs" grow>
                      <div className="app-toolbar__search">
                        <input
                          type="text"
                          placeholder="Search device, serial, event, or script…"
                          value={this.state.searchText}
                          onChange={(e) => this.setState({ searchText: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && this.applySearch()}
                        />
                        <span
                          className="search-icon-button"
                          onClick={() => {
                            this.applySearch();
                          }}
                        >
                          <i className="material-icons">search</i>
                        </span>
                      </div>
                    </AppToolbarField>
                  )}
                  {!hideEventTypeFilter && (
                    <AppToolbarField label="Event type">
                      <select
                        defaultValue=""
                        onChange={this.handleEventTypeSelect}
                        aria-label="Filter by event type"
                      >
                        <option value="" disabled>
                          Select event type…
                        </option>
                        {eventTypeOptions.map(({ label, options }) => (
                          <optgroup key={label} label={label}>
                            {options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </AppToolbarField>
                  )}
                  {showToolbarActions && (
                    <AppToolbarField label={!hideExport && !canClearFilters ? "Export" : "Actions"}>
                      <div className="event-logs__toolbar-actions">
                        {canClearFilters && (
                          <button
                            type="button"
                            className="button secondary app-toolbar__action-button"
                            onClick={this.clearAllFilters}
                          >
                            Clear filters
                          </button>
                        )}
                        {!hideExport && (
                          <button
                            type="button"
                            className="button secondary app-toolbar__action-button event-logs__export-button"
                            onClick={this.handleExportCsv}
                            disabled={this.state.exportingCsv || loading}
                          >
                            <i className="material-icons" aria-hidden="true">
                              download
                            </i>
                            <span>{this.state.exportingCsv ? 'Exporting...' : 'Export CSV'}</span>
                          </button>
                        )}
                      </div>
                    </AppToolbarField>
                  )}
                </AppToolbarGrid>
              )}
              <AppToolbarGrid className="event-logs__timeframe-grid">
                <AppToolbarField label="Timeframe">
                  <select
                    value={timeRangeKey}
                    onChange={this.handleTimePresetChange}
                    aria-label="Timeframe"
                  >
                    {TIMEFRAME_PRESETS.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </AppToolbarField>
              </AppToolbarGrid>
              {filterChips.length > 0 && (
                <div className="event-logs__active-filters">
                  {filterChips}
                </div>
              )}
              {timeRangeKey === 'custom' && (
                <div className="event-logs__custom-range">
                  <AppToolbarField label="Start">
                    <DatePicker
                      selected={customRange[0]}
                      onChange={this.handleCustomStartChange}
                      selectsStart
                      startDate={customRange[0]}
                      endDate={customRange[1]}
                      showTimeSelect
                      dateFormat="Pp"
                      maxDate={customRange[1] || new Date()}
                      className="event-logs__datepicker"
                    />
                  </AppToolbarField>
                  <AppToolbarField label="End">
                    <DatePicker
                      selected={customRange[1]}
                      onChange={this.handleCustomEndChange}
                      selectsEnd
                      startDate={customRange[0]}
                      endDate={customRange[1]}
                      minDate={customRange[0] || null}
                      showTimeSelect
                      dateFormat="Pp"
                      maxDate={new Date()}
                      className="event-logs__datepicker"
                    />
                  </AppToolbarField>
                  <div className="event-logs__custom-range-actions">
                    <button
                      type="button"
                      className="custom-range-action-btn custom-range-action-btn--reset"
                      onClick={this.handleResetCustomRange}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="custom-range-action-btn custom-range-action-btn--apply"
                      onClick={this.handleApplyCustomRange}
                      disabled={disableCustomApply}
                    >
                      Apply
                    </button>
                  </div>
                  {customError && (
                    <div className="event-logs__inline-error">{customError}</div>
                  )}
                </div>
              )}
            </AppToolbar>

            {!hideSummary && summaryWithRange && (
              <div className="event-logs__summary">{summaryWithRange}</div>
            )}

            {loading ? (
              <EventLogsSkeleton isScopedDeviceView={isScopedDeviceView} />
            ) : logs.length > 0 ? (
              <table
                className={["unstriped", this.props.tableClassName].filter(Boolean).join(" ")}
                style={{
                  borderSpacing: compactTable ? '0px' : '0px 3px',
                  borderCollapse: compactTable ? 'collapse' : 'separate',
                  width: compactTable ? '100%' : undefined,
                }}
              >
                <thead>
                  <tr>
                    {!isScopedDeviceView && <th>Device</th>}
                    <th>Operation</th>
                    <th>Status</th>
                    <th>Time</th>
                    <th>Timestamp</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const details = this.renderDetails(log);
                    const typeLabel = this.renderType(log);
                    const baseName = this.getOperationBaseName(log) || typeLabel;
                    const operationPhrase = this.getOperationPhrase(log, baseName);
                    return (
                      <tr key={log.logId}>
                        {!isScopedDeviceView && <td>{this.deviceCell(log)}</td>}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{operationPhrase}</span>
                            {!hideOperationFilterButton && (
                              <button
                                type="button"
                                title="Filter for this event"
                                onClick={() =>
                                  this.handleFilterChange(
                                    isScopedDeviceView
                                      ? { eventId: String(log.eventId) }
                                      : {
                                          query: String(log.enclosureSerialNumber),
                                          searchText: String(log.enclosureSerialNumber),
                                          eventId: String(log.eventId),
                                        }
                                  )
                                }
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: 20,
                                  width: 20,
                                }}
                              >
                                <i className="material-icons" style={{ fontSize: 16, color: '#666' }}>
                                  filter_list
                                </i>
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{this.renderStatus(log)}</td>
                        <td>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {this.renderRelativeTime(log.deviceTs)}
                          </span>
                        </td>
                        <td>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            {this.renderTimestamp(log.deviceTs)}
                          </span>
                        </td>
                        <td style={{ maxWidth: 420 }}>
                          {this.renderDescriptionContent(log, details)}
                        </td>
                        <td>{this.actionsCell(log)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '15px' }}>{emptyStateText}</div>
            )}

            {!hidePagination && (
              <ReactPaginate
                pageCount={meta?.totalPages || 1}
                forcePage={(this.state.page || 1) - 1}
                onPageChange={({ selected }) => this.fetchPage(selected + 1)}
                containerClassName="react-paginate"
                activeClassName="selected"
                previousLabel="Previous"
                nextLabel="Next"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const EventLogsSkeleton = ({ isScopedDeviceView }) => (
  <table className="unstriped" style={{ borderSpacing: '0px 5px' }}>
    <thead>
      <tr>
        {!isScopedDeviceView && <th>Device</th>}
        <th>Operation</th>
        <th>Status</th>
        <th>Time</th>
        <th>Timestamp</th>
        <th>Description</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {[...Array(5)].map((_, i) => (
        <tr key={i}>
          {!isScopedDeviceView && (
            <td>
              <div className="skeleton-blank" />
            </td>
          )}
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
          <td>
            <div className="skeleton-blank" />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);
